import { useCallback } from 'react';

import {
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  Scatter,
  TimeXAxis,
  YAxis,
  Box,
  Stack,
} from '@cast/design-system';

import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import { MetricsBox } from 'components/boxes';
import { useReactRouterStrategy } from 'components/chart/hooks';

import { useOomKillWorkloadsDrawer } from './_hooks/useOomKillWorkloadsDrawer';
import { useWorkloadEventMetricsData } from './_hooks/useWorkloadEventMetricsData';
import { OomKillTooltip } from './OomKillTooltip';
import { OOMKillChartItem } from './types';
import { oomKillsColorMap } from './utils';
import { rowLayoutQuery } from '../_constants';
import {
  useClusterDashboardTimeRangeSelection,
  QUERY_PARAM_KEYS,
} from '../useClusterDashboardTimeRangeSelection';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

const useZoomState = useReactRouterStrategy.bind(null, {
  queryParamsKeys: QUERY_PARAM_KEYS,
});

type Props = {
  timeZone: string;
};

export const WorkloadEventMetricsChart = ({ timeZone }: Props) => {
  const { cluster } = useCluster();
  const { chartData, isLoading, isError, refetch, totals } =
    useWorkloadEventMetricsData();
  const { getRange } = useClusterDashboardTimeRangeSelection();
  const { openOomKillWorkloadsDrawer } = useOomKillWorkloadsDrawer();

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'OOM kill events',
      scopeName: cluster?.name,
      getDefaultRange: getRange,
    }),
  });

  const handleBubbleClick = useCallback(
    (point: OOMKillChartItem, managed: boolean) => {
      const count = managed ? point.managedCount : point.unmanagedCount;
      if (!point.timestamp || count == null) {
        return;
      }
      openOomKillWorkloadsDrawer({
        bucketTimestamp: point.timestamp,
        managed,
        timeZone,
      });
    },
    [openOomKillWorkloadsDrawer, timeZone]
  );

  if (isError) {
    return <FailedToLoad refresh={refetch} />;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
        height: 300,
        [rowLayoutQuery]: {
          flexDirection: 'row',
          height: 215,
        },
      }}
    >
      <Stack
        sx={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          gap: 24,
          [rowLayoutQuery]: {
            flexDirection: 'column',
            gap: 0,
          },
        }}
      >
        <MetricsBox
          title="Total events"
          primaryValue={isLoading ? '--' : totals?.total ?? '--'}
          primaryValueProps={{ variant: 'h6' }}
          titleProps={{ variant: 'L10R' }}
          testId="oom-total-events"
        />
        <Stack
          sx={{
            flexDirection: 'row',
            gap: 24,
            [rowLayoutQuery]: {
              flexDirection: 'column',
              pb: 16,
            },
          }}
        >
          <MetricsBox
            title="Unmanaged workloads"
            primaryValue={isLoading ? '--' : totals?.unmanaged ?? '--'}
            primaryValueProps={{ variant: 'A2' }}
            titleProps={{ variant: 'L10R' }}
            markerColor={oomKillsColorMap.unmanagedCount}
            tooltip="OOMKill events for workloads with Workload Autoscaler disabled. These events are not recorded in the Workload Autoscaler Event Log"
            testId="oom-unmanaged-workloads"
          />
          <MetricsBox
            title="Managed workloads"
            primaryValue={isLoading ? '--' : totals?.managed ?? '--'}
            primaryValueProps={{ variant: 'A2' }}
            titleProps={{ variant: 'L10R' }}
            markerColor={oomKillsColorMap.managedCount}
            tooltip="OOMKill events for workloads with Workload Autoscaler enabled"
            testId="oom-managed-workloads"
          />
        </Stack>
      </Stack>

      <Box flex={1}>
        <ChartContainer
          data={chartData}
          isLoading={isLoading}
          hasNoData={!isLoading && !chartData.length}
          noDataOverlayProps={{ title: 'No events were observed' }}
          testId="workload-event-metrics-chart"
          analyzeWithOpsPilot={analyzeWithOpsPilot}
          zoomProps={{ xAxisDataKey: 'chartTimestampUnix', useZoomState }}
          sx={{ height: '100%' }}
        >
          <ChartGrid hideVertical />
          <Scatter<OOMKillChartItem>
            dataKey="unmanagedCount"
            shapeProps={{
              shapeColor: oomKillsColorMap.unmanagedCount,
              shapeSize: 'small',
            }}
            hideActiveShape
            onShapeClick={(point) => handleBubbleClick(point, false)}
            testId="oom-unmanaged-scatter"
          />
          <Scatter<OOMKillChartItem>
            dataKey="managedCount"
            shapeProps={{
              shapeColor: oomKillsColorMap.managedCount,
              shapeSize: 'small',
            }}
            hideActiveShape
            onShapeClick={(point) => handleBubbleClick(point, true)}
            testId="oom-managed-scatter"
          />
          <TimeXAxis<OOMKillChartItem>
            unixDataKey="chartTimestampUnix"
            timezone={timeZone}
          />
          <YAxis<OOMKillChartItem> allowDecimals={false} />
          <ChartTooltip<OOMKillChartItem>
            TooltipComponent={(props) => (
              <OomKillTooltip {...props} timeZone={timeZone} interactive />
            )}
          />
        </ChartContainer>
      </Box>
    </Box>
  );
};
